import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { selectProduct } from '../../../../store/visualizerSlice';
import Product from '../../Product/Product';
import StepNavigation from '../../StepNavigation/StepNavigation';
import styles from './Carpet.module.css';

const SERIES_ID = '9717';

// Generate level label from style value (L1 -> Level 1, L6 -> Level 6)
const getLevelLabel = (style) => {
    if (!style) return style;
    const match = style.match(/^L(\d+)$/);
    if (match) {
        return `Level ${match[1]}`;
    }
    return style;
};

/**
 * Carpet Step Component
 * Non-applied product - selection is stored but not applied to visualization
 */
export default function Carpet({ 
    stepIndex,
    room = '',
    onViewAllApplied, 
    onNext, 
    onPrevious 
}) {
    const dispatch = useDispatch();
    const stepSelections = useSelector(state => state.visualizer.stepSelections);
    const [productData, setProductData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);

    const selectionKey = `Carpet_${room}`;

    // Restore selection from Redux on mount
    useEffect(() => {
        const savedSelection = stepSelections[selectionKey];
        if (savedSelection?.product) {
            setSelectedProduct(savedSelection.product);
        }
    }, [stepSelections, selectionKey]);

    // Fetch products
    useEffect(() => {
        setLoading(true);
        setError(null);

        const URL = `https://lizard.chameleonpower.com/CDN/ProductsBySeries/${SERIES_ID}.json`;
        
        fetch(URL)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch products: ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                setProductData(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching products:', error);
                setError(error.message);
                setLoading(false);
            });
    }, []);

    // Get unique levels from data and sort them
    const sortedLevels = useMemo(() => {
        const levels = [...new Set(productData.map(p => p.style).filter(Boolean))];
        // Sort by numeric value (L1, L2, L3, L4, L5, ... L10+)
        return levels.sort((a, b) => {
            const numA = parseInt(a.replace('L', '')) || 0;
            const numB = parseInt(b.replace('L', '')) || 0;
            return numA - numB;
        });
    }, [productData]);

    // Group products by level (style attribute)
    // Products without style go to "Included" group at the top
    const groupedProducts = useMemo(() => {
        const groups = {};
        
        // First, add products without tier (Included group)
        const includedProducts = productData.filter(p => !p.style);
        if (includedProducts.length > 0) {
            groups['Included'] = includedProducts;
        }
        
        // Then add tiered products
        sortedLevels.forEach(level => {
            const levelProducts = productData.filter(p => p.style === level);
            if (levelProducts.length > 0) {
                groups[level] = levelProducts;
            }
        });
        
        return groups;
    }, [productData, sortedLevels]);

    const handleProductSelect = (product) => {
        setSelectedProduct(product);
        
        console.log('Carpet - Product selected:', product.color || product.name);
        console.log('Carpet - Non-applied product, storing selection only');
        
        // Store selection in Redux (no apply call)
        dispatch(selectProduct({
            product,
            surfaceName: 'Carpet',
            stepIndex,
            selectionKey,
            nonApplied: true
        }));
    };

    return (
        <div className={styles.container}>
            <StepNavigation
                title="Carpet"
                onNext={onNext}
                onPrevious={onPrevious}
                onViewAllApplied={onViewAllApplied}
            />

            <div className={styles.disclaimer}>
                Carpet products are not visualized
            </div>

            <div className={styles.productListContainer} data-series={SERIES_ID}>
                {loading && (
                    <div className={styles.message}>Loading products...</div>
                )}

                {error && (
                    <div className={styles.errorMessage}>Error: {error}</div>
                )}

                {!loading && !error && Object.keys(groupedProducts).length === 0 && (
                    <div className={styles.message}>No products available.</div>
                )}

                {!loading && !error && Object.entries(groupedProducts).map(([level, products]) => (
                    <div key={level} className={styles.tierSection}>
                        <div className={styles.tierHeader}>
                            <span className={styles.tierTitle}>
                                {level === 'Included' ? 'Included' : getLevelLabel(level)}
                            </span>
                            <div className={styles.tierLine}></div>
                        </div>
                        <ul className={styles.productGrid}>
                            {products.map(product => (
                                <Product
                                    key={product.id}
                                    id={product.id}
                                    specifier={product.specifier}
                                    focus={500}
                                    size="256"
                                    title={product.color || ''}
                                    description={product.name || ''}
                                    series={SERIES_ID}
                                    name={product.name || ''}
                                    color={product.color || ''}
                                    onSelect={(id) => {
                                        const selected = products.find(p => p.id === id);
                                        if (selected) handleProductSelect(selected);
                                    }}
                                    isActive={product.id === selectedProduct?.id}
                                />
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </div>
    );
}

Carpet.propTypes = {
    stepIndex: PropTypes.number.isRequired,
    room: PropTypes.string,
    onViewAllApplied: PropTypes.func,
    onNext: PropTypes.func,
    onPrevious: PropTypes.func
};
